import moment from 'moment'
import {filters} from '@/filters'
import {
  CELL_BORDER, COLORS, DATE_FORMAT, MONEY_FORMAT,
  createWorkbook, downloadWorkbook, styleHeaderRow, toExcelDate,
} from '@/excel'

const TYPE_COLORS = {credit: COLORS.red, debet: COLORS.green}

/**
 * Builds and downloads an .xlsx with a staff member's salary history for a period.
 * @param staff     staff model (name, passport, phone, ...)
 * @param period    {from, to} server-format dates
 * @param remains   month summary: remain_start, debit_credit {debet, credit}, remain_end, worked_days
 * @param rows      all salary rows of the period
 * @param totals    {credit_summa, debet_summa} from the API
 * @param getPaymentType  Payment store getter (row.type -> label)
 * @param workType  staff work type title
 */
export async function exportStaffSalary({staff, period, remains, rows, totals, getPaymentType, workType}) {
  const workbook = await createWorkbook()

  const fullName = filters.full_name(staff).replace(/\s+/g, ' ').trim()

  const sheet = workbook.addWorksheet('Зарплата', {views: [{showGridLines: false}]})
  sheet.columns = [
    {width: 6},
    {width: 14},
    {width: 16},
    {width: 18},
    {width: 50},
  ]

  sheet.addRow([`Карточка сотрудника: ${fullName}`]).font = {bold: true, size: 14}
  sheet.addRow([`Период: ${filters.date(period.from)} — ${filters.date(period.to)}`]).font = {italic: true}

  const info = [
    ['Паспорт', staff.passport],
    ['ПИНФЛ', staff.pinfl],
    ['Телефон', staff.phone ? filters.phone(staff.phone) : null],
    ['Тип', workType],
    ['Ставка', staff.salary_rate],
    ['Описание', staff.description],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '')

  sheet.addRow([])
  info.forEach(([label, value]) => {
    const row = sheet.addRow([label, null, value])
    row.getCell(1).font = {bold: true}
    if (typeof value === 'number') {
      row.getCell(3).numFmt = MONEY_FORMAT
      row.getCell(3).alignment = {horizontal: 'left'}
    }
  })

  sheet.addRow([])
  const summary = [
    ['Остаток на начало', remains.remain_start],
    // credit = accrued salary, debet = paid (same as row types and remain_end = start + credit - debet)
    ['Отработано', remains.debit_credit?.credit],
    ['Оплачено', remains.debit_credit?.debet],
    ['Остаток на конец', remains.remain_end],
    ['Отработанные дни', remains.worked_days],
  ]
  summary.forEach(([label, value]) => {
    const row = sheet.addRow([label, null, null, value ?? 0])
    row.getCell(1).font = {bold: true}
    row.getCell(4).numFmt = MONEY_FORMAT
  })

  sheet.addRow([])
  styleHeaderRow(sheet.addRow(['№', 'Дата', 'Тип', 'Сумма', 'Описание']))

  rows.forEach((item, index) => {
    const row = sheet.addRow([
      index + 1,
      toExcelDate(item.date),
      getPaymentType(item.type).staffListName || item.type,
      item.amount,
      item.comments || '',
    ])
    row.getCell(1).alignment = {horizontal: 'center'}
    row.getCell(2).numFmt = DATE_FORMAT
    row.getCell(2).alignment = {horizontal: 'center'}
    row.getCell(3).alignment = {horizontal: 'center'}
    row.getCell(4).numFmt = MONEY_FORMAT
    row.getCell(5).alignment = {wrapText: true}
    if (TYPE_COLORS[item.type]) {
      row.getCell(3).font = {color: {argb: TYPE_COLORS[item.type]}}
      row.getCell(4).font = {color: {argb: TYPE_COLORS[item.type]}}
    }
    row.eachCell({includeEmpty: true}, cell => cell.border = CELL_BORDER)
  })

  const totalRows = [
    [getPaymentType('credit').staffListName, totals.credit_summa],
    [getPaymentType('debet').staffListName, totals.debet_summa],
  ]
  totalRows.forEach(([label, value]) => {
    const row = sheet.addRow([null, null, `Итого ${String(label).toLowerCase()}:`, value || 0])
    row.font = {bold: true}
    row.getCell(3).alignment = {horizontal: 'right'}
    row.getCell(4).numFmt = MONEY_FORMAT
  })

  await downloadWorkbook(workbook, `Зарплата ${fullName} ${moment(period.from).format('YYYY-MM')}.xlsx`)
}
